/**
 * GamePerformanceStrip - Multi-dimensional game performance chart (Tufte centerpiece).
 *
 * Encodes 5 dimensions per data point:
 * 1. Points scored → Y-position (connected line)
 * 2. Win/Loss → Dot color: green (#00FF88) / red (#FF2D55)
 * 3. Opponent strength → Dot size: elite=5, strong=4, average=3, weak=2.5
 * 4. Season PPG → Thin gray dashed reference line
 * 5. Opponent quality → Subtle background band
 *
 * Sport-agnostic: works for any sport with game scores + opponent strength.
 *
 * @param {Array} games - Recent games [{team_score, result, opponent_abbr, opponent_quality, opp_score, total_points}]
 * @param {number} seasonAvg - Season PPG for reference line
 * @param {number} overUnder - Current game O/U line (optional secondary reference)
 */
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

// Colors
const WIN_COLOR = '#00FF88';
const LOSS_COLOR = '#FF2D55';

// Dot size by opponent quality - pronounced differences
const QUALITY_DOT_SIZE = {
  elite: 8,
  strong: 6,
  average: 4,
  weak: 2.5,
};

// Dot stroke ring for elite/strong to make them pop
const QUALITY_RING = {
  elite: { stroke: '#FFD700', strokeWidth: 1.5 },
  strong: { stroke: '#4488FF', strokeWidth: 1 },
  average: { stroke: 'none', strokeWidth: 0 },
  weak: { stroke: 'none', strokeWidth: 0 },
};

const GamePerformanceStrip = ({
  games = [],
  seasonAvg = null,
  overUnder = null,
}) => {
  if (!games || games.length === 0) return null;

  // Filter out canceled/postponed games (0-0 scores with no result)
  const validGames = games.filter(g => g.team_score > 0 || g.opp_score > 0);
  if (validGames.length === 0) return null;

  const chartData = validGames.map((g, i) => ({
    index: i,
    score: g.team_score,
    label: g.opponent_abbr || `G${i + 1}`,
    result: g.result || (g.won ? 'W' : 'L'),
    quality: g.opponent_quality || 'average',
    oppScore: g.opp_score,
    total: g.total_points,
  }));

  // Calculate Y domain with padding
  const scores = chartData.map(d => d.score).filter(Boolean);
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  const padding = Math.max(5, (maxScore - minScore) * 0.15);
  const yMin = Math.floor(minScore - padding);
  const yMax = Math.ceil(maxScore + padding);

  // Custom dot renderer encoding result + quality
  const renderDot = (props) => {
    const { cx, cy, payload } = props;
    if (cx === undefined || cy === undefined) return null;

    const color = payload.result === 'W' ? WIN_COLOR : LOSS_COLOR;
    const size = QUALITY_DOT_SIZE[payload.quality] || 4;
    const ring = QUALITY_RING[payload.quality] || QUALITY_RING.average;

    return (
      <g>
        {/* Outer ring for elite/strong opponents */}
        {ring.strokeWidth > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={size + ring.strokeWidth}
            fill="none"
            stroke={ring.stroke}
            strokeWidth={ring.strokeWidth}
            opacity={0.7}
          />
        )}
        <circle
          cx={cx}
          cy={cy}
          r={size}
          fill={color}
          stroke="none"
        />
      </g>
    );
  };

  // Custom tooltip with minimal styling
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-bg-primary/95 border border-border rounded px-2 py-1 text-xs">
        <span className={d.result === 'W' ? 'text-success' : 'text-danger'}>
          {d.result}
        </span>
        <span className="text-text-secondary ml-1">
          {d.score}-{d.oppScore} vs {d.label}
        </span>
        <span className="text-text-secondary ml-1 opacity-60">
          ({d.quality})
        </span>
      </div>
    );
  };

  return (
    <div>
      <div style={{ width: '100%', height: 140 }}>
        <ResponsiveContainer>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 12, bottom: 20, left: 4 }}
          >
            {/* Season average reference line */}
            {seasonAvg && (
              <ReferenceLine
                y={seasonAvg}
                stroke="#666"
                strokeDasharray="4 3"
                strokeWidth={0.75}
              />
            )}

            {/* X-axis: opponent abbreviations */}
            <XAxis
              dataKey="label"
              tick={{ fontSize: 8, fill: '#888' }}
              axisLine={false}
              tickLine={false}
              interval={0}
            />

            {/* Y-axis: right side, minimal ticks */}
            <YAxis
              orientation="right"
              domain={[yMin, yMax]}
              tick={{ fontSize: 8, fill: '#666' }}
              axisLine={false}
              tickLine={false}
              tickCount={3}
              width={28}
            />

            <Tooltip content={<CustomTooltip />} />

            {/* Score line with custom colored dots */}
            <Line
              type="monotone"
              dataKey="score"
              stroke="#555"
              strokeWidth={1}
              dot={renderDot}
              activeDot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Inline legend - text only, no chartjunk */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[9px] text-text-secondary mt-0.5 px-1">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-success"></span> W
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-danger"></span> L
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full border border-yellow-500/70" style={{background: 'transparent'}}></span>
          <span className="text-yellow-400">Elite</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full border border-blue-400/70" style={{background: 'transparent'}}></span>
          <span className="text-blue-400">Strong</span>
        </span>
        <span className="opacity-50">Small dot = weak opp</span>
        {seasonAvg && (
          <span className="opacity-50">--- avg ({seasonAvg})</span>
        )}
      </div>
    </div>
  );
};

export default GamePerformanceStrip;
